import Vue from 'vue';
import { tjipDirective, TjipDirectiveData } from './TjipDirective';
import { TjipClipboardService } from './TjipClipboardService';
import { TjipSelectionService } from './TjipSelectionService';
import { IKeyGuardParseResult } from './IKeyGuardParseResult';
import * as _ from 'lodash';
import { ITjipKeyGuardDirectiveOptions } from './ITjipKeyGuardDirectiveOptions';

export class TjipKeyGuardDirectiveDataGeneric<T extends ITjipKeyGuardDirectiveOptions> extends TjipDirectiveData<T> {
  protected inputElement!: HTMLInputElement;
  protected hasFocus = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected value: any;
  protected viewValue: string | undefined = undefined;
  private keypressCallback!: (ev: Event) => void;
  private pasteCallback!: (ev: Event) => void;
  private focusCallback!: (ev: Event) => void;
  private blurCallback!: (ev: Event) => void;
  private changeCallback!: (ev: Event) => void;
  private internalSyntaxInvalid = false;
  private savedSelectionStart: number | null = null;
  private savedSelectionEnd: number | null = null;

  public bind() {
    super.bind();
    this.keypressCallback = this.onKeypress.bind(this);
    this.pasteCallback = this.onPaste.bind(this);
    this.focusCallback = this.onFocus.bind(this);
    this.blurCallback = this.onBlur.bind(this);
    this.changeCallback = this.onChange.bind(this);
    this.inputElement =
      this.el.tagName === 'INPUT'
        ? (this.el as HTMLInputElement)
        : (this.el.querySelector('input') as HTMLInputElement);
    this.inputElement.addEventListener('keypress', this.keypressCallback);
    this.inputElement.addEventListener('paste', this.pasteCallback);
    this.inputElement.addEventListener('focus', this.focusCallback);
    this.inputElement.addEventListener('blur', this.blurCallback);
    this.inputElement.addEventListener('change', this.changeCallback);
  }

  public inserted() {
    super.inserted();
    Object.assign(this.options, this.binding.value);
    Vue.$log.debug('binding', this.binding.value, this.options);
  }

  // public update() {
  //   super.update();
  //   Vue.$log.debug(this.inputValue, this.model.value, this.inputElement.selectionStart);
  //   if (!this.hasFocus) {
  //     this.delayedProcessInputValue();
  //   } else {
  //     // Promise.resolve().then(() => {
  //     //   Vue.$log.debug(this.inputValue, this.model.value);
  //     //   this.updateViewValue();
  //     // });
  //   }
  // }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected lv(value: any): string {
    return `${value} (${typeof value})`;
  }

  public componentUpdated() {
    super.componentUpdated();
    Vue.$log.debug(
      this.id,
      `input: ${this.lv(this.inputValue)} model: ${this.lv(this.model.value)} last value: ${this.lv(
        this.value
      )} selection: ${this.inputElement.selectionStart}-${this.inputElement.selectionEnd}`
    );
    this.saveSelection();
  }

  public unbind() {
    super.unbind();
    this.inputElement.removeEventListener('keypress', this.keypressCallback);
    this.inputElement.removeEventListener('paste', this.pasteCallback);
    this.inputElement.removeEventListener('focus', this.focusCallback);
    this.inputElement.removeEventListener('blur', this.blurCallback);
    this.inputElement.removeEventListener('change', this.changeCallback);
  }

  public get syntaxInvalid(): boolean {
    return this.internalSyntaxInvalid;
  }

  protected get model() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.vnode.data as any).model;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get $v(): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let result = (this.vnode.context as any).$v;
    const expression = this.model.expression;
    const tokens = expression && expression.split('.');
    if (tokens) {
      for (let i1 = 0; i1 < tokens.length; i1++) {
        result = result[tokens[i1]];
      }
    }
    return result;
  }

  // protected get inputElement(): HTMLInputElement {
  //   return this.el as HTMLInputElement;
  // }

  protected get inputValue(): string {
    return this.inputElement.value;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected allowKey(key: string): boolean {
    return this.options.allowedKeys ? new RegExp(`[${this.options.allowedKeys}]`).test(key) : true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected allowValue(value: string): boolean {
    return this.options.allowedValue ? new RegExp(this.options.allowedValue).test(value) : true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected parseValue(value: any): IKeyGuardParseResult {
    return {
      value,
      revert: false,
      invalid: false
    };
  }

  protected transformValue(value: string) {
    if (value) {
      switch (this.options.transform) {
        case 'uppercase':
          return value.toUpperCase();
        case 'lowercase':
          return value.toLowerCase();
      }
    }
    return value;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected formatValue(value: any): string {
    return value;
  }

  protected newValue(insert: string): string {
    const selection = TjipSelectionService.getInputSelection();
    const value = this.inputValue;
    return value.substring(0, selection.start) + insert + value.substring(selection.end);
  }

  protected processInputValue() {
    const newValue = this.transformValue(this.inputValue);
    const v = this.parseValue(newValue);
    Vue.$log.debug(this.id, 'parse', this.lv(newValue), v);
    if (v.revert) {
      return;
    }

    this.value = v.value;
    this.internalSyntaxInvalid = !_.isNil(this.inputValue) && !_.isEmpty(this.inputValue) && v.invalid;

    if (this.hasFocus && v.value !== this.model.value) {
      Vue.$log.debug(this.id, 'emit value', v.value);
      this.componentInstance.$emit('input', v.value);
      // this.delayedUpdateViewValue();
    }
    if (!_.isUndefined(v.viewValue)) {
      this.viewValue = v.viewValue;
    } else if (this.hasFocus) {
      this.viewValue = newValue;
    } else {
      this.viewValue = this.formatValue(v.value);
    }
    this.updateViewValue();
  }

  protected onFocus(ev: Event) {
    this.saveSelection();
    const m = this.model;
    Vue.$log.debug(this.id, 'focus', ev, m, this);
    this.hasFocus = true;
    if (!this.syntaxInvalid) {
      this.viewValue = this.formatValue(this.model.value);
      this.updateViewValue();
    }
  }

  protected onBlur(ev: Event) {
    const m = this.model;
    Vue.$log.debug(this.id, 'blur', ev, m, this);
    this.hasFocus = false;
    if (!this.syntaxInvalid) {
      this.viewValue = this.formatValue(this.model.value);
      this.updateViewValue();
    }
  }

  protected onChange(ev: Event) {
    Vue.$log.debug(this.id, 'change', this.inputValue, this.model.value, ev);
    this.processInputValue();
  }

  protected onKeypress(ev: Event) {
    const isNavigationKeyCode = (keyCode: number) => {
      switch (keyCode) {
        case 8: // BACKSPACE
        case 9: // TAB:
        case 13: // ENTER:
        case 33: // PAGE_UP
        case 34: // PAGE_DOWN
        case 36: // HOME
        case 35: // END
        case 37: // LEFT
        case 39: // RIGHT
        case 38: // UP
        case 40: // DOWN
        case 45: // INSERT
        case 46: // DELETE
          setTimeout(() => {
            this.processInputValue();
          });
          return true;
      }
      return false;
    };

    const isFunctionKeyCode = (keyCode: number) => {
      return keyCode >= 112 /*F1*/ && keyCode <= 123 /*F12*/;
    };

    Vue.$log.debug(this.id, ev);
    const event = ev as KeyboardEvent;
    if (event) {
      const key = event.key || String.fromCharCode(event.keyCode || event.charCode || event.which);
      if (
        !(event.altKey || event.ctrlKey) &&
        !this.allowKey(key) &&
        // always allow Enter key for browser default handling
        key !== 'Enter' &&
        !(!event.charCode /* FF only! */ && (isNavigationKeyCode(event.keyCode) || isFunctionKeyCode(event.keyCode)))
      ) {
        event.stopImmediatePropagation();
        event.preventDefault();
      } else {
        setTimeout(() => {
          this.processInputValue();
        });
      }
    }
  }

  protected onPaste(ev: Event): boolean {
    const pasteValue = TjipClipboardService.getText(ev);
    const newValue = this.newValue(pasteValue);
    const result = this.allowValue(newValue);
    Vue.$log.debug(this.id, 'paste', pasteValue, newValue, result, ev);
    if (!result) {
      ev.stopImmediatePropagation();
      ev.preventDefault();
    } else {
      setTimeout(() => {
        this.processInputValue();
      });
    }
    return result;
  }

  protected updateViewValue() {
    if (typeof this.viewValue !== 'undefined') {
      Vue.$log.debug(this.id, this.inputValue, '->', this.viewValue);
      this.inputElement.value = this.viewValue;
      this.restoreSavedSelection();
    }
  }

  protected delayedUpdateViewValue() {
    Vue.$log.debug(this.id, this.inputValue, this.savedSelectionStart, this.savedSelectionEnd);
    Promise.resolve().then(() => {
      this.updateViewValue();
    });
  }

  protected delayedProcessInputValue() {
    Vue.$log.debug(this.id, this.inputValue, this.savedSelectionStart, this.savedSelectionEnd);
    Promise.resolve().then(() => {
      this.processInputValue();
    });
  }

  private saveSelection() {
    this.savedSelectionStart = this.inputElement.selectionStart;
    this.savedSelectionEnd = this.inputElement.selectionEnd;
    Vue.$log.debug(this.id, this.inputValue, this.savedSelectionStart, this.savedSelectionEnd);
  }

  private restoreSavedSelection() {
    if (this.hasFocus) {
      Vue.$log.debug(
        this.id,
        'restore selection',
        this.inputValue,
        this.savedSelectionStart,
        this.savedSelectionEnd,
        this.inputElement
      );
      this.inputElement.selectionStart = this.savedSelectionStart;
      this.inputElement.selectionEnd = this.savedSelectionEnd;
    }
  }
}

export class TjipKeyGuardDirectiveData extends TjipKeyGuardDirectiveDataGeneric<ITjipKeyGuardDirectiveOptions> {}

export const tjipKeyGuardDirective = tjipDirective(TjipKeyGuardDirectiveData);
